import { Field, FieldError } from '@playbooks/ui/components/field'
import { InputGroupAddon, InputGroupInput } from '@playbooks/ui/components/input-group'
import { ChevronDown, ChevronUp } from 'lucide-react'
import InputGroupWrapper from '../../../components/ui/InputGroupeWrapper'
import { NumericFormat } from 'react-number-format'

interface NumberInputBaseProps {
  id: string
  label: string
  value: string | number
  onChange: (value: number | undefined) => void
  min?: number
  max?: number
  step?: number
  required?: boolean
  invalid?: boolean
  errorMessage?: string
  suffix?: React.ReactNode
  prefix?: React.ReactNode
  placeholder?: string
  testId?: string
  isAmount?: boolean
}

/**
 * Base number input component with increment/decrement buttons
 * Can be extended for specific number types (percentage, currency, etc.)
 */
export default function NumberInputBase({
  id,
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  required = false,
  invalid = false,
  errorMessage,
  suffix,
  prefix,
  placeholder = '',
  testId,
  isAmount,
}: NumberInputBaseProps) {
  // Thousand and decimal separators for the current locale
  const { thousandSeparator, decimalSeparator } = numberLocaleSeparators()

  // Keep the input value within the specified min and max range
  function clamp(val: number) {
    if (val >= max) {
      return max
    }

    if (val <= min) {
      return min
    }

    return val
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target
    const inputValue = isAmount ? parseCurrencyToNumber(input.value) : +input.value
    const newValue = clamp(inputValue)

    onChange(newValue)
  }

  function handleDecrement() {
    const currentValue = value ?? 0

    const inputValue = +currentValue
    const newValue = clamp(inputValue - step)

    onChange(newValue)
  }

  function handleIncrement() {
    const currentValue = value ?? 0
    const inputValue = +currentValue
    const newValue = clamp(inputValue + step)

    onChange(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleIncrement()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleDecrement()
    }
  }

  return (
    <Field data-invalid={invalid} className="relative gap-2">
      <InputGroupWrapper>
        {prefix && <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>}
        <NumericFormat
          data-testid={testId}
          autoComplete="off"
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          defaultValue={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          required={required}
          value={value || ''}
          thousandSeparator={isAmount ? thousandSeparator : undefined}
          decimalSeparator={isAmount ? decimalSeparator : undefined}
          valueIsNumericString
          allowNegative
          customInput={InputGroupInput}
        />
        {suffix && <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon>}
        <InputGroupAddon align="inline-end" className="flex flex-col gap-0">
          <>
            <button tabIndex={-1} type="button" onClick={handleIncrement}>
              <ChevronUp size={16} />
            </button>
            <button tabIndex={-1} type="button" onClick={handleDecrement}>
              <ChevronDown size={16} />
            </button>
          </>
        </InputGroupAddon>
      </InputGroupWrapper>
      {invalid && errorMessage && <FieldError className="absolute -bottom-4 text-xs">{errorMessage}</FieldError>}
    </Field>
  )
}

/**
 * Get the thousand and decimal separators for the current locale
 * @returns An object containing the thousand and decimal separators for the current locale
 */
function numberLocaleSeparators() {
  const { locale } = getLocaleInfo()

  const localeDependedCurrencyFormat = new Intl.NumberFormat(locale).format(111111.1)

  const thousandsChar = localeDependedCurrencyFormat.at(3)
  const decimalChar = localeDependedCurrencyFormat.at(-2)

  // We have to use Unicode period (\u2024) symbol to avoid incorrect formatting in some locales (e.g., German)
  // where the period is used as a thousand separator.
  const thousandSeparator = thousandsChar === '.' ? '\u2024' : thousandsChar
  const decimalSeparator = decimalChar === '.' ? '\u2024' : decimalChar

  return { thousandSeparator, decimalSeparator }
}

function getLocaleInfo() {
  const supportedLangs = ['en', 'fr', 'de', 'es']

  const locale = navigator.language.split('-')[0]
  const defaultLocale = 'en'
  const isSupportedLocale = supportedLangs.includes(locale)

  return { locale: isSupportedLocale ? locale : defaultLocale }
}

/**
 * Locale-specific currency parsing rules
 */
const LOCALE_PARSERS: Record<string, (str: string) => string> = {
  // French locale: 1 234,56 €
  fr: str => str.replace(/\s/g, '').replace(',', '.'),
  // German locale: 1.234,56 €
  de: str => str.replace(/\.|\u2024/g, '').replace(',', '.'),
  // Spanish locale: 1.234,56 €
  es: str => str.replace(/\.|\u2024/g, '').replace(',', '.'),
  // English locale: 1,234.56 €
  en: str => str.replace(/,/g, '').replace(/\.|\u2024/g, '.'),
}

/**
 * Parses a currency string to a number based on the current locale
 * @param str - The currency string to parse
 * @returns The parsed number value
 */
function parseCurrencyToNumber(str: string): number {
  const { locale } = getLocaleInfo()

  const parser = LOCALE_PARSERS[locale] ?? LOCALE_PARSERS['en']
  return Number(parser(str))
}
